import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CheckCircle2, Circle, XCircle, MessageCircle, Flag, RotateCcw, Send, Euro } from 'lucide-react';
import i18n from '../../i18n';
import type { ConsultazioneItem, RichiestaDetail } from '../../api/richieste';
import { concludiOrdine, revocaConferma, riapriOrdine, riportaAGenerazione, inviaOmologaProduttore } from '../../api/richieste';
import { listConferimenti } from '../../api/conferimenti';
import { canCommerciale, canChiusuraOrdine } from '../../lib/constants';
import Modal from '../ui/Modal';
import StatusBadge from '../ui/StatusBadge';
import AnnullaRichiestaModal from './AnnullaRichiestaModal';
import GeneraPreventivoOmologaWizard from './GeneraPreventivoOmologaWizard';
import InviaClienteModal from './InviaClienteModal';
import ConfermaModal from './ConfermaModal';
import InProgressModal from './InProgressModal';
import ApriConsultazioneModal from './ApriConsultazioneModal';
import RispondiConsultazioneModal from './RispondiConsultazioneModal';
import ModificaCondizioniModal from './ModificaCondizioniModal';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTimeIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatEurKg(v: number | null): string {
  if (v == null) return '—';
  return `${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 5 })} €/kg`;
}

// ── Stepper logic (nuovo flusso a 5 step #95) ────────────────────────────────

type StepState = 'done' | 'active' | 'pending' | 'cancelled';

interface Step {
  label: string;
  sublabel: string;
  state: StepState;
}

// Sequenza canonica del flusso nuovo, escluso lo stato laterale `in_consultazione`
// (rappresentato come badge sovrapposto, vedi `Stepper`) e lo stato terminale `annullato`.
const STEP_ORDER = [
  'ricevuto',
  'preventivo_omologa_generati',
  'inviato_cliente',
  'confermato',
  'in_progress',
] as const;

type FlowStatus = (typeof STEP_ORDER)[number];

function effectiveStatus(r: RichiestaDetail): FlowStatus | 'annullato' {
  // Se in consultazione: usa lo stato_pre_consultazione per posizionare lo stepper
  // (la consultazione è laterale e l'ordine ci tornerà alla risposta).
  if (r.status === 'in_consultazione') {
    const open = r.consultazioni.find((c) => c.stato === 'aperta');
    const pre = open?.stato_pre_consultazione ?? 'ricevuto';
    if ((STEP_ORDER as readonly string[]).includes(pre)) return pre as FlowStatus;
    return 'ricevuto';
  }
  if (r.status === 'annullato') return 'annullato';
  // 'concluso' (#106) è oltre l'ultimo step del flusso: lo posizioniamo su in_progress.
  if (r.status === 'concluso') return 'in_progress';
  if ((STEP_ORDER as readonly string[]).includes(r.status)) return r.status as FlowStatus;
  return 'ricevuto';
}

function cancelledAtStep(r: RichiestaDetail): number {
  // Su quale step lo stepper deve marcare la "X" rossa.
  // Ricaviamo dallo storico audit dove eravamo arrivati: il più avanzato.
  if (r.omologa_ricevuta_at || r.status === 'in_progress') return 4; // step "In lavorazione"
  if (r.preventivo_confermato_at) return 3; // step "Confermato"
  if (r.preventivo_inviato_at) return 2;    // step "Inviato cliente"
  if (r.tariffa_unitaria_eur_kg != null) return 1; // step "Preventivo+omologa generati"
  return 0; // step "Ricevuto" — i18n-ignore: commento
}

function buildSteps(r: RichiestaDetail): Step[] {
  const eff = effectiveStatus(r);
  const isCancelled = eff === 'annullato';
  const cancelStep = isCancelled ? cancelledAtStep(r) : -1;
  const currentIdx = isCancelled
    ? -1
    : STEP_ORDER.indexOf(eff as FlowStatus);

  function st(n: number): StepState {
    if (isCancelled) {
      if (n < cancelStep) return 'done';
      if (n === cancelStep) return 'cancelled';
      return 'pending';
    }
    if (n < currentIdx) return 'done';
    if (n === currentIdx) return 'active';
    return 'pending';
  }

  // label/sublabel = chiavi i18n (tradotte nello Stepper).
  return [
    { label: 'richieste.workflow.step_ricevuto', sublabel: 'richieste.workflow.sub_amm', state: st(0) },
    { label: 'richieste.workflow.step_preventivo', sublabel: 'richieste.workflow.sub_amm', state: st(1) },
    { label: 'richieste.workflow.step_inviato', sublabel: 'richieste.workflow.sub_amm', state: st(2) },
    { label: 'richieste.workflow.step_confermato', sublabel: 'richieste.workflow.sub_cliente_firma', state: st(3) },
    { label: 'richieste.workflow.step_in_lavorazione', sublabel: 'richieste.workflow.sub_produttore_firma', state: st(4) },
  ];
}

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') return <CheckCircle2 size={18} className="text-accent-success" />;
  if (state === 'cancelled') return <XCircle size={18} className="text-accent-danger" />;
  if (state === 'active') return <Circle size={18} className="text-sage fill-sage/20" />;
  return <Circle size={18} className="text-stone-border" />;
}

function Stepper({ steps, consultazioneAperta }: { steps: Step[]; consultazioneAperta: ConsultazioneItem | null }) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="flex items-start gap-0">
        {steps.map((step, i) => (
          <div key={step.label} className="flex items-start flex-1 min-w-0">
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className="flex items-center w-full">
                {i > 0 && (
                  <div
                    className={`flex-1 h-0.5 ${
                      steps[i - 1].state === 'done'
                        ? 'bg-accent-success'
                        : steps[i - 1].state === 'cancelled'
                        ? 'bg-accent-danger'
                        : 'bg-stone-mid'
                    }`}
                  />
                )}
                <div className="flex-shrink-0">
                  <StepIcon state={step.state} />
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 ${
                      step.state === 'done'
                        ? 'bg-accent-success'
                        : step.state === 'cancelled'
                        ? 'bg-accent-danger'
                        : 'bg-stone-mid'
                    }`}
                  />
                )}
              </div>
              <div className="mt-1 w-full text-center px-0.5">
                <p
                  title={t(step.label)}
                  className={`text-[10px] font-medium leading-tight break-words hyphens-auto ${
                    step.state === 'active'
                      ? 'text-sage'
                      : step.state === 'done'
                      ? 'text-accent-success'
                      : step.state === 'cancelled'
                      ? 'text-accent-danger'
                      : 'text-ink-faint'
                  }`}
                >
                  {t(step.label)}
                </p>
                {step.sublabel && (
                  <p className="text-[9px] text-ink-faint leading-tight">{t(step.sublabel)}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Badge laterale `in_consultazione` (non è uno step ma uno stato bloccante) */}
      {consultazioneAperta && (
        <div className="absolute -top-2 right-0 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#F5F3FF] text-[#7C3AED] border border-[#DDD6FE] text-[10px] font-semibold">
          <MessageCircle size={11} />
          {t('richieste.workflow.in_consultazione_badge', { tipo: consultazioneAperta.tipo })}
        </div>
      )}
    </div>
  );
}

// ── Audit log (campi nuovi) ──────────────────────────────────────────────────

function buildAuditLog(r: RichiestaDetail): string[] {
  const tt = i18n.t;
  const nd = tt('richieste.workflow.nd');
  const entries: string[] = [];
  if (r.tariffa_unitaria_eur_kg != null) {
    entries.push(tt('richieste.workflow.audit_preventivo', { tariffa: formatEurKg(r.tariffa_unitaria_eur_kg) }));
  }
  // Trasporto: riga di STATO CORRENTE (presente solo se attivo), come la riga
  // tariffa qui sopra — NON un evento. Lo storico attivato/disattivato/
  // modificato del trasporto non ha storage (solo log strutturato BE): se
  // servirà a video, tabella eventi economici dedicata, lotto a sé.
  if (!r.cliente_porta_materiale && r.costo_trasporto_eur_per_viaggio != null && r.costo_trasporto_eur_per_viaggio > 0) {
    entries.push(tt('richieste.workflow.audit_trasporto', {
      costo: r.costo_trasporto_eur_per_viaggio.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    }));
  }
  // Revisioni tariffa (quote_revisions): valori arrotondati a 2 decimali dal
  // NUMERIC(10,2) dell'audit DB — la tariffa piena resta su orders.
  for (const rev of r.revisioni_tariffa ?? []) {
    entries.push(tt('richieste.workflow.audit_tariffa_modificata', {
      da: formatEurKg(rev.importo_precedente),
      a: formatEurKg(rev.importo_nuovo),
      nome: rev.revised_da_nome ?? nd,
      data: formatDateTimeIT(rev.revised_at),
    }));
  }
  if (r.preventivo_inviato_at) {
    entries.push(tt('richieste.workflow.audit_inviato', { data: formatDateTimeIT(r.preventivo_inviato_at) }));
  }
  if (r.preventivo_confermato_at) {
    entries.push(tt('richieste.workflow.audit_confermato', { data: formatDateTimeIT(r.preventivo_confermato_at) }));
  }
  if (r.omologa_ricevuta_at) {
    let line = tt('richieste.workflow.audit_omologa', { data: formatDateTimeIT(r.omologa_ricevuta_at) });
    if (r.omologa_produttore_data_firma) {
      line += tt('richieste.workflow.audit_omologa_firma', { data: formatDateIT(r.omologa_produttore_data_firma) });
    }
    if (r.omologa_produttore_data_scadenza) {
      line += tt('richieste.workflow.audit_omologa_scade', { data: formatDateIT(r.omologa_produttore_data_scadenza) });
    }
    entries.push(line);
  }
  for (const c of r.consultazioni) {
    const apertura = tt('richieste.workflow.audit_cons_apertura', { tipo: c.tipo, nome: c.richiesta_da_nome ?? nd, data: formatDateTimeIT(c.richiesta_at) });
    if (c.stato === 'aperta') {
      entries.push(tt('richieste.workflow.audit_cons_attesa', { apertura }));
    } else {
      entries.push(tt('richieste.workflow.audit_cons_risposta', { apertura, nome: c.risposta_da_nome ?? nd, data: formatDateTimeIT(c.risposta_at) }));
    }
  }
  if (r.annullato_at) {
    entries.push(
      tt('richieste.workflow.audit_annullato', { nome: r.annullato_da_nome ?? nd, data: formatDateTimeIT(r.annullato_at) }) +
        (r.motivazione_annullamento ? tt('richieste.workflow.audit_motivazione', { motivo: r.motivazione_annullamento }) : ''),
    );
  }
  return entries;
}

// ── Props ────────────────────────────────────────────────────────────────────

interface WorkflowCardProps {
  richiesta: RichiestaDetail;
  userRole: string;
  onUpdated: (updated: RichiestaDetail) => void;
  onToast: (msg: string) => void;
}

type ModalOpen =
  | 'genera_preventivo_omologa'
  | 'invia_cliente'
  | 'conferma'
  | 'in_progress'
  | 'apri_consultazione'
  | 'rispondi_consultazione'
  | 'annulla'
  | 'modifica_condizioni'
  | null;

const ANNULLABILI = new Set([
  'ricevuto',
  'preventivo_omologa_generati',
  'inviato_cliente',
  'confermato',
  'in_consultazione',
  'in_progress',
]);

// ── Component ────────────────────────────────────────────────────────────────

export default function WorkflowCard({
  richiesta,
  userRole,
  onUpdated,
  onToast,
}: WorkflowCardProps) {
  const { t } = useTranslation();
  const [modal, setModal] = useState<ModalOpen>(null);
  // #106: vincolo concludi — esiste un conferimento pre-stoccaggio?
  const [hasPreStoccaggio, setHasPreStoccaggio] = useState(false);
  const [concluding, setConcluding] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [confirmRiapri, setConfirmRiapri] = useState(false);
  // V68 — revoca della conferma: azione distruttiva sui documenti, richiede
  // una modale che dichiari le conseguenze PRIMA di procedere.
  const [revoking, setRevoking] = useState(false);
  const [confirmRevoca, setConfirmRevoca] = useState(false);
  const [reportingBack, setReportingBack] = useState(false);
  const [confirmRiporta, setConfirmRiporta] = useState(false);
  const [sendingOmolProd, setSendingOmolProd] = useState(false);
  const [showCerWarning, setShowCerWarning] = useState(false);
  const [confirmOmolProd, setConfirmOmolProd] = useState(false);

  const r = richiesta;
  const s = r.status;
  const consultazioneAperta = r.consultazioni.find((c) => c.stato === 'aperta') ?? null;
  const steps = buildSteps(r);
  const auditLog = buildAuditLog(r);

  // Concludi/riapri/revoca-conferma: un insieme solo, quello della require_role
  // del backend, identica sui tre endpoint. Era l'unione `isAMMorAdmin ||
  // canComm`, con un nome che non nominava COMMERCIALE.
  const canChiudere = canChiusuraOrdine(userRole);
  // Flusso commerciale: {admin, COMMERCIALE, DIR, RT} (AMM escluso, opzione A).
  const canComm = canCommerciale(userRole);
  const canAnnulla = canComm && ANNULLABILI.has(s);

  // In consultazione le transizioni sono bloccate. Solo "Rispondi" disponibile,
  // a chi di competenza (admin/RT/DIR per tipo matching).
  const isInConsultazione = s === 'in_consultazione';
  const isConsultedActor =
    !!consultazioneAperta &&
    ((consultazioneAperta.tipo === 'RT' && userRole === 'RT') ||
      (consultazioneAperta.tipo === 'DIR' && userRole === 'DIR'));
  const canRispondereConsultazione =
    !!consultazioneAperta && (userRole === 'admin' || isConsultedActor);

  // #107d: durante una consultazione AMM/admin e l'attore consultato possono
  // (ri)generare preventivo/omologa senza uscire dallo stato in_consultazione.
  // Ammesso solo se lo stato "sotto" la consultazione lo consente.
  const statoPre = consultazioneAperta?.stato_pre_consultazione ?? '';
  const canEditInConsultazione =
    isInConsultazione &&
    (canComm || isConsultedActor) &&
    ['ricevuto', 'preventivo_omologa_generati'].includes(statoPre);

  // Transizioni AMM disponibili per stato (escludo in_consultazione)
  const canGenera = !isInConsultazione && s === 'ricevuto' && canComm;
  // #5: senza CER assegnato il BE rifiuta la generazione (422). Gating cosmetico:
  // disabilita l'azione con una nota, l'autorità resta il backend.
  const noCer = !r.cer_code || r.cer_code.trim() === '';
  // #107b: rigenerazione pre-invio (riapre lo stesso wizard).
  const canRigenera = !isInConsultazione && s === 'preventivo_omologa_generati' && canComm;
  const canInvia = !isInConsultazione && s === 'preventivo_omologa_generati' && canComm;
  const canConferma = !isInConsultazione && s === 'inviato_cliente' && canComm;
  const canInProgress = !isInConsultazione && s === 'confermato' && canComm;
  // F4: guardia precondizione — senza omologa associata (omologa_id NULL, dato
  // legacy) il BE rifiuta in-progress al submit: l'azione va bloccata PRIMA di
  // far caricare il PDF e scegliere la data.
  const inProgressSenzaOmologa = canInProgress && !r.omologa;
  // Micro-label: se l'omologa associata è già firmata, l'upload in-progress non
  // è necessario → label senza "(carica omologa firmata)".
  const omologaGiaFirmata = r.omologa?.stato_firma === 'firmata' && !!r.omologa?.pdf_firmato_url;
  const canApriConsultazione =
    !isInConsultazione &&
    canComm &&
    ['ricevuto', 'preventivo_omologa_generati', 'inviato_cliente', 'confermato'].includes(s);

  // #106: "Segna concluso" disponibile in in_progress; disabilitato finché ci
  // sono conferimenti pre-stoccaggio (programmato/in_transito/attesa_compliance).
  const canConcludi = s === 'in_progress' && canChiudere;
  // Riapertura: solo su 'concluso', stesso set di ruoli del concludi (AMM incluso
  // via isAMMorAdmin). NON allarga canCommerciale.
  const canRiapri = s === 'concluso' && canChiudere;
  // #7: passo indietro inviato_cliente → preventivo_omologa_generati. Sempre
  // disponibile da inviato_cliente, ruoli di genera/invia-cliente (canComm). I
  // documenti già prodotti NON vengono toccati.
  const canRiporta = !isInConsultazione && s === 'inviato_cliente' && canComm;
  // V68 — solo da `confermato`. `in_consultazione` escluso anche quando
  // `stato_pre_consultazione` vale 'confermato': prima si chiude la
  // consultazione. Gate cosmetico, l'autorita' resta il require_role del BE.
  const canRevocaConferma = !isInConsultazione && s === 'confermato' && canChiudere;
  // Modifica condizioni economiche (tariffa/trasporto) post-conferma: SOLO
  // {confermato, in_progress, concluso} — negli stati precedenti la strada
  // resta la rigenerazione del preventivo. `in_consultazione` escluso (s non è
  // nel set). Ordini di ACQUISTO esclusi: le loro condizioni si gestiscono dal
  // prospetto fornitore (il BE risponde 422). Stessi ruoli di
  // genera-preventivo (canComm, AMM escluso). Gate cosmetico, autorità al BE.
  const canModificaCondizioni =
    ['confermato', 'in_progress', 'concluso'].includes(s) && canComm && r.tipo_operazione !== 'acquisto';
  // #8: invio omologa al produttore — azione autonoma (slegata dallo stato).
  // Visibile per produttore DISTINTO dal cliente (con produttore_coincide=true resta
  // assente: l'azione non avrebbe senso). Se manca un prerequisito è visibile ma
  // DISABILITATO, con tooltip per-motivo (precedenza: omologa a monte dell'email).
  const showInviaOmolProd = canComm && r.produttore_coincide === false;
  const omolProdMancaOmologa = !r.omologa;
  const omolProdMancaEmail = !r.omologa ? false : !r.produttore?.email;
  const omolProdDisabled = omolProdMancaOmologa || omolProdMancaEmail;
  const omolProdTooltip = omolProdMancaOmologa
    ? t('richieste.workflow.omologa_non_generata')
    : omolProdMancaEmail
      ? t('richieste.workflow.manca_email_produttore')
      : undefined;
  useEffect(() => {
    if (!canConcludi) return;
    let attivo = true;
    void (async () => {
      try {
        const res = await listConferimenti({ order_id: r.id, limit: 200 });
        if (attivo) {
          setHasPreStoccaggio(res.items.some((c) =>
            ['programmato', 'in_transito', 'attesa_compliance'].includes(c.status)));
        }
      } catch { /* non bloccare la card */ }
    })();
    return () => { attivo = false; };
  }, [canConcludi, r.id]);

  function handleSaved(updated: RichiestaDetail, toastMsg: string) {
    setModal(null);
    onUpdated(updated);
    onToast(toastMsg);
  }

  async function handleConcludi() {
    setConcluding(true);
    try {
      const updated = await concludiOrdine(r.id);
      onUpdated(updated);
      onToast(t('richieste.workflow.toast_concluso'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      onToast(typeof detail === 'string' ? detail : t('richieste.workflow.errore_concludi'));
    } finally {
      setConcluding(false);
    }
  }

  async function handleRiapri() {
    setReopening(true);
    try {
      const updated = await riapriOrdine(r.id);
      setConfirmRiapri(false);
      onUpdated(updated);
      onToast(t('richieste.workflow.toast_riaperto'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      onToast(typeof detail === 'string' ? detail : t('richieste.workflow.errore_riapri'));
    } finally {
      setReopening(false);
    }
  }

  async function handleRevocaConferma() {
    setRevoking(true);
    try {
      const updated = await revocaConferma(r.id);
      setConfirmRevoca(false);
      onUpdated(updated);
      onToast(t('richieste.workflow.toast_conferma_revocata'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const d = detail as { conferimenti?: string[]; fatture?: string[]; message?: string };
        const elenco = [...(d.conferimenti ?? []), ...(d.fatture ?? [])].join(', ');
        onToast(elenco ? `${d.message ?? ''} ${elenco}`.trim() : (d.message ?? ''));
      } else {
        onToast(typeof detail === 'string' ? detail : t('richieste.workflow.errore_revoca_conferma'));
      }
    } finally {
      setRevoking(false);
    }
  }

  async function handleRiporta() {
    setReportingBack(true);
    try {
      const updated = await riportaAGenerazione(r.id);
      setConfirmRiporta(false);
      onUpdated(updated);
      onToast(t('richieste.workflow.toast_riportato'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      onToast(typeof detail === 'string' ? detail : t('richieste.workflow.errore_riporta'));
    } finally {
      setReportingBack(false);
    }
  }

  async function handleInviaOmolProd() {
    setSendingOmolProd(true);
    try {
      const updated = await inviaOmologaProduttore(r.id);
      setConfirmOmolProd(false);
      onUpdated(updated);
      onToast(t('richieste.workflow.toast_omologa_produttore'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      onToast(typeof detail === 'string' ? detail : t('richieste.workflow.errore_omologa_produttore'));
    } finally {
      setSendingOmolProd(false);
    }
  }

  // #5: bottone sempre cliccabile; senza CER apre un avviso e NON apre il wizard.
  // Il 422 BE resta l'autorità.
  function openGenera() {
    if (noCer) { setShowCerWarning(true); return; }
    setModal('genera_preventivo_omologa');
  }

  return (
    <>
      <div data-testid="workflow-card" className="bg-white border border-stone-mid rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-dark">{t('richieste.workflow.titolo')}</h3>
          <StatusBadge status={s} size="sm" />
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Stepper */}
          <div data-testid="workflow-stepper" className="pt-3">
            <Stepper steps={steps} consultazioneAperta={consultazioneAperta} />
          </div>

          {/* Audit log */}
          {auditLog.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-1.5">
                {t('richieste.workflow.cronologia')}
              </p>
              <ul className="space-y-1">
                {auditLog.map((entry, i) => (
                  <li key={i} className="text-xs text-ink-mid leading-relaxed">
                    {entry}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Consultazione aperta: dettaglio + azione Rispondi */}
          {consultazioneAperta && (
            <div className="rounded-lg border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-3">
              <div className="flex items-start gap-2 mb-2">
                <MessageCircle size={14} className="text-[#7C3AED] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#7C3AED] uppercase tracking-wide">
                    {t('richieste.workflow.consultazione_aperta', { tipo: consultazioneAperta.tipo })}
                  </p>
                  <p className="text-xs text-ink-soft">
                    <Trans
                      i18nKey="richieste.workflow.consultazione_da"
                      values={{
                        nome: consultazioneAperta.richiesta_da_nome ?? t('richieste.workflow.nd'),
                        data: formatDateTimeIT(consultazioneAperta.richiesta_at),
                        stato: consultazioneAperta.stato_pre_consultazione ?? '—',
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                </div>
              </div>
              <p className="text-sm text-ink-mid whitespace-pre-wrap mb-2">
                {consultazioneAperta.commento_richiesta}
              </p>
              {canRispondereConsultazione && (
                <button
                  type="button"
                  onClick={() => setModal('rispondi_consultazione')}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#7C3AED] rounded-lg hover:bg-[#6D28D9] transition-colors"
                >
                  {t('richieste.workflow.rispondi_consultazione')}
                </button>
              )}
              {canEditInConsultazione && (
                <button
                  type="button"
                  data-testid="genera-in-consultazione"
                  onClick={() => setModal('genera_preventivo_omologa')}
                  className="w-full mt-2 px-4 py-2 text-sm font-medium text-[#7C3AED] border border-[#DDD6FE] bg-white rounded-lg hover:bg-[#EDE9FE] transition-colors"
                >
                  {statoPre === 'preventivo_omologa_generati'
                    ? t('richieste.workflow.btn_rigenera')
                    : t('richieste.workflow.btn_genera')}
                </button>
              )}
            </div>
          )}

          {/* Azioni */}
          {s !== 'annullato' && (
            <div className="space-y-2 pt-1 border-t border-stone-mid">
              {canGenera && (
                <button
                  type="button"
                  onClick={openGenera}
                  data-testid="btn-genera"
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
                >
                  {t('richieste.workflow.btn_genera')}
                </button>
              )}
              {canInvia && (
                <button
                  type="button"
                  onClick={() => setModal('invia_cliente')}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
                >
                  {t('richieste.workflow.btn_invia')}
                </button>
              )}
              {canRigenera && (
                <button
                  type="button"
                  onClick={openGenera}
                  data-testid="btn-rigenera"
                  className="w-full px-4 py-2 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
                >
                  {t('richieste.workflow.btn_rigenera')}
                </button>
              )}
              {canConferma && (
                <button
                  type="button"
                  onClick={() => setModal('conferma')}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
                >
                  {t('richieste.workflow.btn_conferma')}
                </button>
              )}
              {canInProgress && (inProgressSenzaOmologa ? (
                <div>
                  <button
                    type="button"
                    disabled
                    data-testid="in-progress-bloccato"
                    className="w-full px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg opacity-50 cursor-not-allowed"
                  >
                    {t('richieste.workflow.btn_in_lavorazione')}
                  </button>
                  <p className="text-xs text-accent-danger mt-1.5">
                    {t('richieste.workflow.in_lavorazione_bloccato')}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setModal('in_progress')}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
                >
                  {omologaGiaFirmata
                    ? t('richieste.workflow.btn_in_lavorazione')
                    : t('richieste.workflow.btn_in_lavorazione_carica')}
                </button>
              ))}
              {canApriConsultazione && (
                <button
                  type="button"
                  onClick={() => setModal('apri_consultazione')}
                  className="w-full px-4 py-2 text-sm font-medium text-[#7C3AED] border border-[#DDD6FE] bg-[#F5F3FF] rounded-lg hover:bg-[#EDE9FE] transition-colors"
                >
                  {t('richieste.workflow.btn_apri_consultazione')}
                </button>
              )}
              {canConcludi && (
                <button
                  type="button"
                  onClick={handleConcludi}
                  disabled={hasPreStoccaggio || concluding}
                  title={hasPreStoccaggio
                    ? t('richieste.workflow.concludi_bloccato')
                    : undefined}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Flag size={14} /> {concluding ? t('richieste.workflow.conclusione_in_corso') : t('richieste.workflow.btn_concludi')}
                </button>
              )}
              {canRiapri && (
                <button
                  type="button"
                  data-testid="btn-riapri"
                  onClick={() => setConfirmRiapri(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
                >
                  <RotateCcw size={14} /> {t('richieste.workflow.btn_riapri')}
                </button>
              )}
              {canRevocaConferma && (
                <button
                  type="button"
                  data-testid="btn-revoca-conferma"
                  onClick={() => setConfirmRevoca(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-accent-warning border border-accent-warning rounded-lg hover:bg-accent-warning-bg transition-colors"
                >
                  <RotateCcw size={14} /> {t('richieste.workflow.btn_revoca_conferma')}
                </button>
              )}
              {canRiporta && (
                <button
                  type="button"
                  data-testid="btn-riporta"
                  onClick={() => setConfirmRiporta(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
                >
                  <RotateCcw size={14} /> {t('richieste.workflow.btn_riporta')}
                </button>
              )}
              {canModificaCondizioni && (
                <button
                  type="button"
                  data-testid="btn-modifica-condizioni"
                  onClick={() => setModal('modifica_condizioni')}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
                >
                  <Euro size={14} /> {t('richieste.workflow.btn_modifica_condizioni')}
                </button>
              )}
              {showInviaOmolProd && (
                <button
                  type="button"
                  data-testid="btn-invia-omologa-produttore"
                  disabled={omolProdDisabled}
                  title={omolProdTooltip}
                  onClick={() => setConfirmOmolProd(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Send size={14} /> {t('richieste.workflow.btn_omologa_produttore')}
                </button>
              )}
              {canAnnulla && (
                <button
                  type="button"
                  onClick={() => setModal('annulla')}
                  className="w-full px-4 py-2.5 text-sm font-medium text-accent-danger bg-accent-danger-bg border border-[#FECACA] rounded-lg hover:bg-[#FEE2E2] transition-colors"
                >
                  {t('richieste.workflow.btn_annulla')}
                </button>
              )}
              {/* Suggerimenti RBAC: utenti senza permessi sullo stato corrente */}
              {!canGenera && !canInvia && !canConferma && !canInProgress && !canApriConsultazione && !canAnnulla && !canConcludi && !canRiapri && !canRiporta && !canModificaCondizioni && !showInviaOmolProd && !isInConsultazione && (
                <p className="text-xs text-ink-soft italic text-center py-2">
                  {t('richieste.workflow.nessuna_azione')}
                </p>
              )}
            </div>
          )}

          {s === 'annullato' && r.motivazione_annullamento && (
            <div className="pt-1 border-t border-stone-mid">
              <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-1">
                {t('richieste.workflow.motivo_annullamento')}
              </p>
              <p className="text-xs text-ink-mid">{r.motivazione_annullamento}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal === 'genera_preventivo_omologa' && (
        <GeneraPreventivoOmologaWizard
          richiestaId={r.id}
          produttoreId={r.produttore?.id ?? null}
          customer={r.customer}
          isRigenera={s === 'preventivo_omologa_generati' || statoPre === 'preventivo_omologa_generati'}
          tariffaAttuale={r.tariffa_unitaria_eur_kg}
          costoTrasportoAttuale={r.costo_trasporto_eur_per_viaggio}
          numeroTrasportiAttuale={r.numero_trasporti_stimati}
          validitaAttuale={r.validita_preventivo_giorni}
          notePreventivoAttuali={r.note_preventivo}
          clientePortaMaterialeAttuale={r.cliente_porta_materiale}
          modalitaConferimento={r.modalita_conferimento}
          tipoOperazioneAttuale={r.tipo_operazione}
          categoriaTariffaDefault={r.categoria_tariffa_default}
          categoriaTipoOperazioneDefault={r.categoria_tipo_operazione_default}
          omologaAssociataId={r.omologa?.id ?? null}
          onClose={() => setModal(null)}
          onProduttoreMaterializzato={onUpdated}
          onSaved={(u) =>
            handleSaved(u, (s === 'preventivo_omologa_generati' || statoPre === 'preventivo_omologa_generati')
              ? t('richieste.workflow.toast_rigenerati')
              : t('richieste.workflow.toast_generati'))
          }
        />
      )}
      {modal === 'invia_cliente' && (
        <InviaClienteModal
          richiestaId={r.id}
          codiceOrdine={r.codice_ordine}
          emailDefault={r.email_richiesta}
          onClose={() => setModal(null)}
          onSaved={(u, warning) =>
            handleSaved(u, warning ?? t('richieste.workflow.toast_inviati'))
          }
        />
      )}
      {modal === 'conferma' && (
        <ConfermaModal
          richiestaId={r.id}
          onClose={() => setModal(null)}
          onSaved={(u) => handleSaved(u, t('richieste.workflow.toast_confermato'))}
        />
      )}
      {modal === 'in_progress' && (
        <InProgressModal
          richiestaId={r.id}
          omologa={r.omologa}
          onClose={() => setModal(null)}
          onSaved={(u) => handleSaved(u, t('richieste.workflow.toast_omologa_registrata'))}
        />
      )}
      {modal === 'apri_consultazione' && (
        <ApriConsultazioneModal
          richiestaId={r.id}
          onClose={() => setModal(null)}
          onSaved={(u) => handleSaved(u, t('richieste.workflow.toast_consultazione_aperta'))}
        />
      )}
      {modal === 'rispondi_consultazione' && consultazioneAperta && (
        <RispondiConsultazioneModal
          consultazione={consultazioneAperta}
          onClose={() => setModal(null)}
          onSaved={(u) => handleSaved(u, t('richieste.workflow.toast_risposta_inviata'))}
        />
      )}
      {modal === 'annulla' && (
        <AnnullaRichiestaModal
          richiestaId={r.id}
          onClose={() => setModal(null)}
          onSaved={(u) => handleSaved(u, t('richieste.workflow.toast_annullata'))}
        />
      )}
      {modal === 'modifica_condizioni' && (
        <ModificaCondizioniModal
          richiestaId={r.id}
          tariffaAttuale={r.tariffa_unitaria_eur_kg}
          trasportoAbilitatoAttuale={!r.cliente_porta_materiale}
          costoTrasportoAttuale={r.costo_trasporto_eur_per_viaggio}
          onClose={() => setModal(null)}
          onSaved={(u) => {
            // La modale resta aperta per l'esito (bozze coinvolte): niente
            // handleSaved, che la chiuderebbe.
            onUpdated(u);
            onToast(t('richieste.workflow.toast_condizioni_modificate'));
          }}
        />
      )}
      {confirmRevoca && (
        <Modal
          title={t('richieste.workflow.revoca_conferma_titolo')}
          onClose={() => setConfirmRevoca(false)}
          testid="revoca-conferma-modal"
          footer={(
            <>
              <button type="button" onClick={() => setConfirmRevoca(false)} disabled={revoking}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                {t('common.azioni.annulla')}
              </button>
              <button type="button" onClick={handleRevocaConferma} disabled={revoking}
                data-testid="btn-conferma-revoca"
                className="px-4 py-2 text-sm font-medium text-white bg-accent-warning rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60">
                {revoking ? t('richieste.workflow.revoca_in_corso') : t('richieste.workflow.btn_revoca_conferma')}
              </button>
            </>
          )}
        >
          {/* Le conseguenze si dichiarano PRIMA: non e' un'azione da fare per sbaglio. */}
          <p className="text-sm text-ink-dark">{t('richieste.workflow.revoca_conferma_intro')}</p>
          <ul className="text-sm text-ink-mid list-disc pl-5 space-y-1">
            <li>{t('richieste.workflow.revoca_conseguenza_documenti')}</li>
            <li>{t('richieste.workflow.revoca_conseguenza_omologa')}</li>
            <li>{t('richieste.workflow.revoca_conseguenza_rigenerare')}</li>
          </ul>
        </Modal>
      )}
      {confirmRiapri && (
        <Modal
          title={t('richieste.workflow.riapri_titolo')}
          onClose={() => setConfirmRiapri(false)}
          testid="riapri-modal"
          footer={(
            <>
              <button type="button" onClick={() => setConfirmRiapri(false)} disabled={reopening}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                {t('common.azioni.annulla')}
              </button>
              <button type="button" onClick={handleRiapri} disabled={reopening} data-testid="btn-conferma-riapri"
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
                {reopening ? t('richieste.workflow.riapertura_in_corso') : t('richieste.workflow.btn_riapri')}
              </button>
            </>
          )}
        >
          <p className="text-sm text-ink-mid">{t('richieste.workflow.riapri_conferma')}</p>
          {r.fatturato_ordine && (
            <p className="text-xs text-ink-soft" data-testid="riapri-nota-fatture">
              {t('richieste.workflow.riapri_nota_fatture')}
            </p>
          )}
        </Modal>
      )}
      {confirmRiporta && (
        <Modal
          title={t('richieste.workflow.riporta_titolo')}
          onClose={() => setConfirmRiporta(false)}
          testid="riporta-modal"
          footer={(
            <>
              <button type="button" onClick={() => setConfirmRiporta(false)} disabled={reportingBack}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                {t('common.azioni.annulla')}
              </button>
              <button type="button" onClick={handleRiporta} disabled={reportingBack} data-testid="btn-conferma-riporta"
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
                {reportingBack ? t('richieste.workflow.riporta_in_corso') : t('richieste.workflow.btn_riporta')}
              </button>
            </>
          )}
        >
          <p className="text-sm text-ink-mid">{t('richieste.workflow.riporta_conferma')}</p>
        </Modal>
      )}
      {confirmOmolProd && (
        <Modal
          title={t('richieste.workflow.omologa_produttore_titolo')}
          onClose={() => setConfirmOmolProd(false)}
          testid="omologa-produttore-modal"
          footer={(
            <>
              <button type="button" onClick={() => setConfirmOmolProd(false)} disabled={sendingOmolProd}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                {t('common.azioni.annulla')}
              </button>
              <button type="button" onClick={handleInviaOmolProd} disabled={sendingOmolProd} data-testid="btn-conferma-omologa-produttore"
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
                {sendingOmolProd ? t('richieste.workflow.omologa_produttore_in_corso') : t('richieste.workflow.btn_omologa_produttore')}
              </button>
            </>
          )}
        >
          <p className="text-sm text-ink-mid">{t('richieste.workflow.omologa_produttore_conferma')}</p>
          <p className="text-xs text-ink-soft mt-1" data-testid="omologa-produttore-destinatario">
            {t('richieste.workflow.omologa_produttore_destinatario', { email: r.produttore?.email })}
          </p>
        </Modal>
      )}
      {showCerWarning && (
        <Modal
          title={t('richieste.workflow.cer_warning_titolo')}
          onClose={() => setShowCerWarning(false)}
          testid="cer-warning-modal"
          footer={(
            <button type="button" onClick={() => setShowCerWarning(false)} data-testid="btn-cer-warning-ok"
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors">
              {t('richieste.workflow.cer_warning_ok')}
            </button>
          )}
        >
          <p className="text-sm text-ink-mid">{t('richieste.workflow.genera_no_cer')}</p>
        </Modal>
      )}
    </>
  );
}
